<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useTicketsForEventQuery, useCartMutation, useTicketCashPaymentMutation } from '@/features/tickets/api/queries'
import { useUsersInfiniteQuery } from '@/enitites/user/api/queries'
import { useImage } from '@/shared/composables/image'
import { useCart } from '@/features/tickets/composables/useCart'
import { usePricing } from '@/features/tickets/composables/usePricing'
import useValidationRules from '@/shared/composables/validation'
import { ModalWrapper } from '@/shared/ui/modals'
import PurchaseTicketItem from '@/features/tickets/ui/PurchaseTicketItem.vue'
import OrderSummary from '@/features/tickets/ui/OrderSummary.vue'
import BKInput from '@/components/AppShared/BKInput.vue'
import BKButton from '@/components/AppShared/BKButton.vue'
import AvatarPlaceholder from '@/features/tickets/assets/AvatarPlaceholder.vue'
import { UserDB, UsersDBSearchParams } from '@/enitites/user'
import { useEventQuery } from '@/enitites/event/api/queries'
import { mdiMagnify } from '@mdi/js'
import ConfirmationCheck from '@/assets/icons/ConfirmationCheck.vue'
import { IFrontdeskRegistrant } from '@/features/tickets/types'

const props = defineProps<{
  eventId: number
  value: boolean
  prefilledUser?: IFrontdeskRegistrant
}>()

const emit = defineEmits<{
  (e: 'input', value: boolean): void
  (e: 'close-modal'): void
  (e: 'attendee-added'): void
  (e: 'manage-ticket', ticketId: number): void
}>()

const { getImageById } = useImage()
const rules = useValidationRules()

// Event and tickets data
const { data: event, isLoading: isLoadingEvent, isError: isEventError } = useEventQuery(props.eventId)
const { data: tiers, isLoading: isLoadingTickets, isError: isTicketsError } = useTicketsForEventQuery(props.eventId)
const { mutate: postCart, isPending: isPostingCart, error: errorPostingCart, data: cartSummary } = useCartMutation()
const {
  mutate: postCashPayment,
  isPending: isPostingCashPayment,
  // error: errorPostingCashPayment,
  data: paymentResult,
} = useTicketCashPaymentMutation()

// User search for assignment
const userSearchParams = ref<UsersDBSearchParams>({
  pageNumber: 0,
  pageSize: 50,
  keyword: '',
})
const { data: usersData, isFetching: isLoadingUsers } = useUsersInfiniteQuery(userSearchParams.value)

const STEPS = {
  CHOOSE_TICKETS: 'Choose tickets',
  CHECKOUT: 'Check out',
} as const

const currentStep = ref<keyof typeof STEPS>('CHOOSE_TICKETS')
const tickets = computed(() => tiers.value?.tiers || [])
const users = computed(() => (usersData.value ? usersData.value.pages.flatMap((page) => page) : []))

const { cart, updateCart, isCartEmpty, clearCart, updateContactInfo } = useCart()
const { getTicketById } = usePricing(tickets, cart)

const selectedUser = ref<Partial<UserDB> | string | null>(null)
const fullNameForEmail = ref<string>('')

const isCheckoutDisabled = computed(() => isCartEmpty.value)

// Initialize selectedUser with prefilledUser when modal opens
watch(
  () => props.value,
  (isOpen) => {
    if (isOpen && props.prefilledUser) {
      // Convert IFrontdeskRegistrant to a format compatible with selectedUser
      selectedUser.value = {
        id: props.prefilledUser.userId,
        dancerName: props.prefilledUser.dancerName,
        aEmail: props.prefilledUser.email,
        avatar: props.prefilledUser.avatar,
      }
      fullNameForEmail.value = props.prefilledUser.dancerName
    }
  },
  { immediate: true }
)

watch(
  [selectedUser, fullNameForEmail],
  ([user, fullName]) => {
    let contactEmail = ''
    let contactName = ''

    if (typeof user === 'object' && user && 'aEmail' in user && user.aEmail) {
      contactEmail = user.aEmail
      contactName = user.dancerName || ''
    } else if (typeof user === 'string') {
      contactEmail = user
      contactName = fullName || ''
    }

    updateContactInfo(contactEmail, contactName)
  },
  { immediate: true }
)

const validateUser = (value: unknown) => {
  if (typeof value === 'string') {
    return rules.email(value)
  }
  if (value && typeof value === 'object' && ('id' in value || 'userId' in value)) {
    return true
  }
  return 'Please select a valid user or enter a valid email address.'
}

const closeModal = () => {
  clearCart()
  currentStep.value = 'CHOOSE_TICKETS'
  selectedUser.value = null
  fullNameForEmail.value = ''

  emit('input', false)
  emit('close-modal')
}

const handleManageTicket = () => {
  if (paymentResult.value?.success) {
    emit('manage-ticket', paymentResult.value?.tickets?.[0]?.id)
    closeModal()
  }
}

const handlePurchaseWithCash = () => {
  if (isPostingCart.value || isPostingCashPayment.value || !cartSummary.value || !selectedUser.value) return

  const userId =
    typeof selectedUser.value === 'object' && 'id' in selectedUser.value && selectedUser.value.id
      ? selectedUser.value.id
      : undefined

  postCashPayment({
    eventId: props.eventId,
    data: {
      ...cartSummary.value,
      contactName: cart.value.contactName || 'Not set',
      contactEmail: cart.value.contactEmail || 'Not set',
      userId,
    },
  })
}

watch(
  () => cart.value.tiers,
  (newTiers) => {
    if (newTiers.length > 0) {
      postCart({
        eventId: props.eventId,
        data: {
          tiers: newTiers,
          contactEmail: cart.value.contactEmail,
          contactName: cart.value.contactName,
          discountCode: cart.value.discountCode,
          paymentOption: 'cash',
        },
      })
    }
  },
  { deep: true }
)

const updateUserSelectorKey = ref(0)
const handleSelectedUserChange = () => {
  updateUserSelectorKey.value += 1
}

const getTicketQuantityFromCart = (ticketId: number): number => {
  const tier = cart.value.tiers.find((t) => t.tierId === ticketId)
  return tier ? tier.quantity : 0
}

const getAgeByBirthdate = (birthdate: string): number => {
  const today = new Date()
  const birthDate = new Date(birthdate)
  let age = today.getFullYear() - birthDate.getFullYear()
  const monthDifference = today.getMonth() - birthDate.getMonth()
  if (monthDifference < 0 || (monthDifference === 0 && today.getDate() < birthDate.getDate())) {
    age -= 1
  }
  return age
}

const getItemDisplayText = (item: string | Partial<UserDB>): string => {
  if (typeof item === 'string') {
    return item
  }
  if (item.aEmail) {
    return item.aEmail
  }
  return 'Unassigned'
}

const getUserLocationText = (user: Partial<UserDB>): string => {
  let location = user.city || ''
  if (user.state) {
    location += ` ${user.state}`
  }
  if (user.country) {
    location += `, ${user.country}`
  }
  return location
}
</script>

<template>
  <ModalWrapper
    :is-show="value"
    modal-width="1000px"
    @update:isShow="closeModal"
  >
    <v-card class="add-attendee-modal">
      <!-- Success Confirmation -->
      <div
        v-if="paymentResult?.success"
        class="success-confirmation"
      >
        <div class="success-content">
          <h2 class="success-title">Payment successful</h2>
          <ConfirmationCheck />
          <BKButton
            text
            width="auto"
            @click="handleManageTicket"
          >
            Manage ticket
          </BKButton>
        </div>
      </div>

      <!-- Normal Purchase Interface -->
      <div
        v-else
        class="purchase-interface-container"
      >
        <div class="purchase-ticket-interface">
          <div class="header">
            <template v-if="isLoadingEvent">
              <v-skeleton-loader
                type="image"
                height="84"
                width="84"
              />
              <div class="skeleton-info">
                <v-skeleton-loader type="heading" />
                <v-skeleton-loader
                  type="text@2"
                  width="150"
                />
              </div>
            </template>

            <template v-else-if="isEventError">
              <p class="error-message">Error loading event data</p>
            </template>
            <template v-else-if="event">
              <v-img
                :src="getImageById(event.image, 168, 168, 'event')"
                height="84"
                width="84"
                class="event-image"
              />
              <div class="event-info">
                <h2 class="event-title">{{ event.title }}</h2>
                <p class="event-info-row">{{ event.dateFlat }}</p>
                <p class="event-info-row">{{ event.timeFlat }}</p>
              </div>
            </template>
          </div>

          <div class="tickets">
            <v-tabs
              v-model="currentStep"
              background-color="transparent"
              color="var(--color-dark-800)"
              class="tabs"
              grow
            >
              <v-tab tab-value="CHOOSE_TICKETS">
                {{ STEPS.CHOOSE_TICKETS }}
              </v-tab>
              <v-tab
                tab-value="CHECKOUT"
                :disabled="isCheckoutDisabled"
              >
                {{ STEPS.CHECKOUT }}
              </v-tab>
            </v-tabs>
            <v-tabs-items
              v-model="currentStep"
              class="tabs-items"
              touchless
            >
              <v-tab-item
                value="CHOOSE_TICKETS"
                class="tickets-list"
              >
                <template v-if="isLoadingTickets">
                  <p>Loading tickets...</p>
                </template>
                <template v-else-if="isTicketsError">
                  <p class="error-message">Error loading tickets.</p>
                </template>
                <template v-else-if="tickets && tickets.length">
                  <PurchaseTicketItem
                    v-for="ticket in tickets"
                    :key="ticket.id"
                    :ticket="ticket"
                    :current-quantity="getTicketQuantityFromCart(ticket.id)"
                    @update:quantity="updateCart"
                  />
                </template>
                <template v-else>
                  <p>No tickets available for this event.</p>
                </template>
              </v-tab-item>
              <v-tab-item value="CHECKOUT">
                <div class="checkout-form">
                  <p class="checkout-title">Contact information</p>

                  <div class="select-wrapper">
                    <v-combobox
                      :key="updateUserSelectorKey"
                      v-model="selectedUser"
                      :append-icon="mdiMagnify"
                      class="user-selector"
                      color="var(--color-dark-800)"
                      eager
                      height="56"
                      hide-details="auto"
                      :items="users"
                      item-color="var(--color-dark-800)"
                      item-value="id"
                      item-text="dancerName"
                      :loading="isLoadingUsers"
                      :menu-props="{
                        attach: '.select-wrapper',
                        contentClass: 'user-select-menu',
                        eager: true,
                        maxHeight: 279,
                      }"
                      no-filter
                      outlined
                      placeholder="Search users by name or email"
                      return-object
                      :rules="[validateUser]"
                      :search-input.sync="userSearchParams.keyword"
                      validate-on-blur
                      @change="handleSelectedUserChange"
                    >
                      <template #selection="{ item }">
                        <div
                          v-if="typeof item === 'object' && item.dancerName"
                          class="selected-item"
                        >
                          <AvatarPlaceholder
                            v-if="!item.avatar"
                            background-color="#DDDDDD"
                            color="#333333"
                            size="24"
                          />
                          <v-avatar
                            v-else
                            size="24"
                          >
                            <v-img :src="getImageById(item.avatar, 48, 48, 'avatar')" />
                          </v-avatar>
                          <span>{{ item.dancerName }}</span>
                        </div>
                        <span
                          v-else
                          class="selected-item"
                        >
                          <AvatarPlaceholder
                            background-color="#DDDDDD"
                            color="#333333"
                            size="24"
                          />
                          {{ getItemDisplayText(item) }}
                        </span>
                      </template>
                      <template #no-data>
                        <div class="no-data-item">
                          <v-progress-circular
                            v-if="isLoadingUsers"
                            indeterminate
                            color="var(--color-blue-800)"
                          />
                          <span v-else>
                            <b>No users found</b><br />
                            If the user doesn't have a profile, use their email to send and assign ticket.
                          </span>
                        </div>
                      </template>
                      <template #item="{ item }">
                        <div class="select-item">
                          <AvatarPlaceholder
                            v-if="!item.avatar"
                            background-color="#DDDDDD"
                            color="#333333"
                          />
                          <v-avatar
                            v-else
                            size="20"
                          >
                            <v-img :src="getImageById(item.avatar, 48, 48, 'avatar')" />
                          </v-avatar>
                          <span>{{ item.dancerName }}</span>
                        </div>
                      </template>
                    </v-combobox>
                  </div>

                  <div
                    v-if="selectedUser && typeof selectedUser === 'object'"
                    class="selected-user-card"
                  >
                    <div class="selected-user">
                      <v-avatar size="100">
                        <v-img :src="getImageById(selectedUser.avatar || '', 200, 200, 'avatar')" />
                      </v-avatar>

                      <div class="selected-user-info">
                        <p class="selected-user-dancer-name">
                          {{ selectedUser.dancerName }}
                        </p>
                        <p class="selected-user-full-name">
                          {{ selectedUser.fullName
                          }}{{ selectedUser.birthday ? `, ${getAgeByBirthdate(selectedUser.birthday)}` : '' }}
                        </p>
                        <p class="selected-user-location">
                          {{ getUserLocationText(selectedUser) }}
                        </p>
                      </div>
                    </div>
                    <div class="confirmation-message">
                      <strong>Make sure</strong> this is the profile you want to assign the ticket to
                    </div>
                  </div>

                  <div
                    v-if="selectedUser && typeof selectedUser === 'string'"
                    class="selected-user-email"
                  >
                    <div class="confirmation-message">
                      <b>Can't find a profile for this email.</b> We'll use email to assign and send ticket.
                    </div>
                    <BKInput
                      v-model="fullNameForEmail"
                      placeholder="Add recipient's full name"
                      :rules="[rules.required]"
                    />
                  </div>
                </div>
              </v-tab-item>
            </v-tabs-items>
          </div>

          <div class="actions">
            <BKButton
              v-if="currentStep === 'CHOOSE_TICKETS'"
              white-text
              :disabled="isCheckoutDisabled"
              @click="currentStep = 'CHECKOUT'"
            >
              Checkout
            </BKButton>
            <template v-if="currentStep === 'CHECKOUT'">
              <BKButton
                outlined
                width="auto"
                @click="currentStep = 'CHOOSE_TICKETS'"
              >
                Go Back
              </BKButton>
              <BKButton
                white-text
                width="auto"
                color="var(--color-green)"
                :disabled="!selectedUser || (typeof selectedUser === 'string' && !fullNameForEmail.trim())"
                :loading="isPostingCashPayment"
                @click="handlePurchaseWithCash"
              >
                Purchase
              </BKButton>
            </template>
          </div>

          <div
            v-if="errorPostingCart"
            class="error-message"
          >
            {{ errorPostingCart?.message }}
          </div>

          <OrderSummary
            class="summary-area"
            :cart-summary="isCartEmpty ? undefined : cartSummary"
            :get-ticket-by-id="getTicketById"
            :is-loading="isPostingCart"
          />
        </div>
      </div>
    </v-card>
  </ModalWrapper>
</template>

<style scoped lang="scss">
.add-attendee-modal {
  max-height: 90vh;
  box-shadow: none !important;
}

.success-confirmation {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  height: 100%;
  padding: 40px;
}

.success-content {
  flex: 1 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  text-align: center;
}

.success-title {
  font-weight: 700;
  font-size: 20px;
  line-height: 26px;
  color: var(--color-dark-800);
  margin: 0;
}

.purchase-interface-container {
  container-type: inline-size;
}

.purchase-ticket-interface {
  display: grid;
  grid-template-areas:
    'header  summary'
    'tickets summary'
    'actions summary'
    'error   summary';
  grid-template-columns: 1fr 304px;
  grid-template-rows: auto 1fr auto;
  max-height: 90vh;
  overflow: hidden;

  p {
    margin: 0;
  }

  @container (max-width: 800px) {
    grid-template-areas:
      'header'
      'tickets'
      'summary'
      'actions';
    grid-template-columns: 1fr;
    grid-template-rows: auto auto auto auto;
    max-height: unset;
  }

  @container (max-width: 600px) {
    padding-top: 50px;
  }
}

.header {
  position: relative;
  grid-area: header;
  display: flex;
  gap: 16px;
  padding: 40px;
  width: 100%;

  &::after {
    content: '';
    position: absolute;
    bottom: -3px;
    left: 0;
    width: 100%;
    height: 3px;
    background-image: linear-gradient(to bottom, rgb(0 0 0 / 10%), transparent);
  }

  .skeleton-info {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: space-around;
    gap: 8px;
  }

  .event-image {
    flex: 0 0 auto;
    border-radius: 8px;
  }

  .event-info {
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 3px;

    .event-title {
      font-weight: var(--font-weight-semi-bold);
      font-size: 24px;
      line-height: 30px;
      color: var(--color-dark-800);
    }

    .event-info-row {
      font-size: 16px;
      line-height: 24px;
      color: var(--color-grey-300);
    }
  }
}

.tickets {
  grid-area: tickets;
  display: grid;
  grid-template-rows: auto 1fr;
  gap: 20px;
  padding: 30px 40px;
  overflow-y: auto;

  @container (max-width: 600px) {
    padding: 16px 12px;
  }

  .v-tab {
    text-transform: unset;

    &.v-tab--active {
      font-weight: 700;
    }
  }

  .v-window {
    overflow: visible;
  }

  .tickets-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
}

.checkout-form {
  display: flex;
  flex-direction: column;
  gap: 12px;

  .checkout-title {
    font-weight: 700;
    font-size: 24px;
    line-height: 30px;
  }
}

.select-wrapper {
  position: relative;

  .selected-item {
    font-size: 16px;
    line-height: 24px;
    color: var(--color-dark-800);
    display: flex;
    align-items: center;
    gap: 4px;

    .hint-mark {
      color: var(--color-grey-300);
    }
  }

  .user-selector {
    border-radius: 6px;
    border-color: var(--color-grey-300);

    :deep(.v-input__slot) {
      background-color: var(--color-white);
    }

    &.v-select--is-menu-active {
      border-radius: 6px 6px 0 0;
      border: 1px solid var(--color-grey-300) !important;

      :deep(*) {
        border: none !important;
      }

      .selected-item {
        display: none;
      }
    }
  }

  .select-item {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 16px;
    line-height: 24px;
    color: var(--color-dark-800);
  }

  .no-data-item {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 12px 24px;
    font-size: 16px;
    line-height: 24px;
    color: var(--color-dark-800);
  }
}

.selected-user-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 38px;

  .selected-user {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 20px;

    .selected-user-info {
      display: flex;
      flex-direction: column;
      gap: 4px;

      .selected-user-dancer-name {
        font-weight: 500;
        font-size: 20px;
        line-height: 1.3;
        color: var(--color-dark-800);
        margin: 0;
      }

      .selected-user-full-name,
      .selected-user-location {
        font-size: 14px;
        line-height: 20px;
        color: var(--color-grey-300);
        margin: 0;
      }
    }
  }

  .confirmation-message {
    max-width: 220px;
    font-size: 14px;
    line-height: 20px;
    color: var(--color-grey-300);
    text-align: center;
    text-wrap: balance;
  }
}

.selected-user-email {
  display: flex;
  flex-direction: column;
  gap: 12px;

  .confirmation-message {
    font-size: 12px;
    line-height: 14px;
    color: var(--color-dark-800);
  }
}

.actions {
  grid-area: actions;
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
  padding: 30px 40px 40px;
  border-top: 1px solid var(--color-grey-700);

  > * {
    flex: 1 1 0;
    min-width: fit-content;
  }
}

.error-message {
  grid-area: error;
  padding: 0 40px 20px;
  color: var(--error-red);
  font-size: 14px;
}

.summary-area {
  grid-area: summary;
}

:deep(.user-select-menu) {
  position: static;
  border-radius: 0 0 6px 6px;
  border: 1px solid var(--color-grey-300);
  border-top: none;
  box-shadow: none;
  min-width: 100% !important;

  .v-list-item {
    padding: 0 12px;
  }
}
</style>
